'use client';

import React from 'react';
import { X, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Seat, Space } from '../../types';

interface SeatLayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  space: Space;
  seats: Seat[];
  onSelectSeat?: (seat: Seat) => void;
  selectedSeatId?: string;
}

export function SeatLayoutModal({
  isOpen,
  onClose,
  space,
  seats,
  onSelectSeat,
  selectedSeatId
}: SeatLayoutModalProps) {
  if (!isOpen) return null;

  const spaceSeats = seats.filter(s => s.space_id === space.id);

  const getStatusColor = (status: Seat['status']) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 border-green-500 text-green-700 hover:bg-green-200 cursor-pointer';
      case 'occupied':
        return 'bg-gray-200 border-gray-400 text-gray-500 cursor-not-allowed';
      case 'reserved':
        return 'bg-yellow-100 border-yellow-500 text-yellow-700 cursor-not-allowed';
      case 'my-reserved':
        return 'bg-blue-100 border-blue-500 text-blue-700 cursor-pointer';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };

  const getStatusLabel = (status: Seat['status']) => {
    switch (status) {
      case 'available': return '빈 자리';
      case 'occupied': return '사용중';
      case 'reserved': return '예약됨';
      case 'my-reserved': return '내 예약';
      default: return '';
    }
  };

  const getThemeColor = () => {
    switch (space.theme) {
      case 'purple': return 'bg-purple-500';
      case 'orange': return 'bg-orange-500';
      case 'green': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const handleSeatClick = (seat: Seat) => {
    if (seat.status === 'available' || seat.status === 'my-reserved') {
      onSelectSeat?.(seat);
    }
  };

  // 포커스룸: 2x5 그리드
  const renderFocusRoomLayout = () => (
    <div className="grid grid-cols-5 gap-2 p-4">
      {spaceSeats.map((seat) => (
        <button
          key={seat.id}
          onClick={() => handleSeatClick(seat)}
          disabled={seat.status === 'occupied' || seat.status === 'reserved'}
          className={`
            w-12 h-12 rounded-lg border-2 flex items-center justify-center
            font-bold text-sm transition-all
            ${getStatusColor(seat.status)}
            ${selectedSeatId === seat.id ? 'ring-2 ring-purple-500 ring-offset-2' : ''}
          `}
        >
          {seat.number}
        </button>
      ))}
    </div>
  );

  // 스터디룸: 2x2 그리드 (테이블 형태)
  const renderStudyRoomLayout = () => (
    <div className="p-4">
      <div className="relative w-48 h-32 mx-auto">
        {/* 테이블 */}
        <div className="absolute inset-8 bg-orange-200 rounded-lg border-2 border-orange-300" />

        {/* 좌석 배치 */}
        {spaceSeats.map((seat, index) => {
          const positions = [
            'top-0 left-1/2 -translate-x-1/2', // 상단
            'bottom-0 left-1/2 -translate-x-1/2', // 하단
            'left-0 top-1/2 -translate-y-1/2', // 왼쪽
            'right-0 top-1/2 -translate-y-1/2', // 오른쪽
          ];
          return (
            <button
              key={seat.id}
              onClick={() => handleSeatClick(seat)}
              disabled={seat.status === 'occupied' || seat.status === 'reserved'}
              className={`
                absolute w-10 h-10 rounded-full border-2 flex items-center justify-center
                font-bold text-sm transition-all
                ${positions[index]}
                ${getStatusColor(seat.status)}
                ${selectedSeatId === seat.id ? 'ring-2 ring-orange-500 ring-offset-2' : ''}
              `}
            >
              {seat.number}
            </button>
          );
        })}
      </div>
    </div>
  );

  // 오픈석: 6x5 그리드
  const renderOpenSeatLayout = () => (
    <div className="grid grid-cols-6 gap-1.5 p-4">
      {spaceSeats.map((seat) => (
        <button
          key={seat.id}
          onClick={() => handleSeatClick(seat)}
          disabled={seat.status === 'occupied' || seat.status === 'reserved'}
          className={`
            w-9 h-9 rounded border-2 flex items-center justify-center
            font-medium text-xs transition-all
            ${getStatusColor(seat.status)}
            ${selectedSeatId === seat.id ? 'ring-2 ring-green-500 ring-offset-1' : ''}
          `}
        >
          {seat.number}
        </button>
      ))}
    </div>
  );

  const renderLayout = () => {
    if (space.id === 'focus') return renderFocusRoomLayout();
    if (space.id === 'group-a' || space.id === 'group-b') return renderStudyRoomLayout();
    if (space.id === 'open') return renderOpenSeatLayout();
    return null;
  };

  const availableCount = spaceSeats.filter(s => s.status === 'available').length;
  const occupiedCount = spaceSeats.filter(s => s.status === 'occupied').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-sm max-h-[80vh] overflow-hidden shadow-xl">
        {/* 헤더 */}
        <div className={`${getThemeColor()} text-white p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <h2 className="text-lg font-bold">{space.name} 배치도</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm opacity-90 mt-1">
            사용중 {occupiedCount}석 / 빈자리 {availableCount}석
          </p>
        </div>

        {/* 배치도 */}
        <div className="p-4 bg-gray-50">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* 입구 표시 */}
            <div className="bg-gray-100 py-2 text-center text-sm text-gray-500 border-b">
              ↑ 입구
            </div>

            {renderLayout()}
          </div>
        </div>

        {/* 범례 */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex flex-wrap gap-3 justify-center text-xs">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-green-100 border-2 border-green-500 rounded" />
              <span>빈자리</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-gray-200 border-2 border-gray-400 rounded" />
              <span>사용중</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-yellow-100 border-2 border-yellow-500 rounded" />
              <span>예약됨</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-blue-100 border-2 border-blue-500 rounded" />
              <span>내 예약</span>
            </div>
          </div>
        </div>

        {/* 닫기 버튼 */}
        <div className="p-4 border-t">
          <Button onClick={onClose} className="w-full" variant="outline">
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
}
